import {
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
  useRef,
  useEffect,
} from "react";
import type { ConversationMessage } from "../types";

const PLACEHOLDER =
  "Tell Quanty about your spicy discoveries, deals, or questionable salads...";

type Props = {
  messages: ConversationMessage[];
  input: string;
  onInputChange: (value: string) => void;
  onSubmit: () => void;
  disabled: boolean;
  status: string;
  ready: boolean;
  onReset: () => void;
};

export function ChatPanel({
  messages,
  input,
  onInputChange,
  onSubmit,
  disabled,
  status,
  ready,
  onReset,
}: Props) {
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    console.debug("ChatPanel.handleSubmit", { input, disabled });
    if (!input.trim() || disabled) {
      return;
    }
    console.debug("ChatPanel: submitting via form");
    onSubmit();
  }

  function handleChange(event: ChangeEvent<HTMLTextAreaElement>) {
    onInputChange(event.target.value);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    // Pressing Enter sends the message, Shift+Enter inserts a newline
    if (event.key === "Enter") {
      console.debug("ChatPanel.handleKeyDown", {
        key: event.key,
        shiftKey: event.shiftKey,
        input,
        disabled,
      });
    }

    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (!input.trim() || disabled) return;
      console.debug("ChatPanel: submitting via Enter key");
      onSubmit();
    }
  }

  return (
    <section className="chat-panel">
      <div className="" ref={listRef}>
        {messages.map((msg) => (
          <article key={msg.id} className={`bubble bubble-${msg.role}`}>
            <span>{msg.text}</span>
            {msg.reaction && (
              <small className="reaction-tag">{msg.reaction}</small>
            )}
          </article>
        ))}
      </div>
      <form className="chat-input" onSubmit={handleSubmit}>
        <textarea
          value={input}
          onChange={handleChange}
          placeholder={PLACEHOLDER}
          rows={3}
          onFocus={() => onInputChange(input)}
          onKeyDown={handleKeyDown}
        />
        <button type="submit" disabled={disabled || !input.trim()}>
          {disabled ? "Thinking..." : "Send"}
        </button>
      </form>
      <button type="button" className="ghost" onClick={onReset}>
        Reset conversation
      </button>
    </section>
  );
}
